"use client";

import { useSession, useSupabaseClient } from "@/components/SupabaseProvider";
import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { parseScheduleDefaults } from "../components/publicationFlow/scheduleConfig";

export const scheduleDefaultsQueryKey = (userId: string | undefined) => ["schedule-defaults", userId] as const;

/** The user's schedule defaults, which a new project opens with. The API keeps
 * them when a schedule is saved; this only reads them. */
export function useScheduleDefaults() {
  const supabase = useSupabaseClient();
  const userId = useSession()?.user.id;
  const loadedUser = useRef({ id: userId, ready: false });
  if (loadedUser.current.id !== userId) loadedUser.current = { id: userId, ready: false };
  const query = useQuery({
    queryKey: scheduleDefaultsQueryKey(userId),
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles")
        .select("schedule_defaults").eq("id", userId!).maybeSingle();
      if (error) throw error;
      return parseScheduleDefaults(data?.schedule_defaults ?? null);
    },
    enabled: Boolean(userId),
    refetchOnMount: "always",
  });
  // Wait for this entry's own successful read, as for the resource config.
  // Opening without the defaults would let the next Save replace them with
  // guessed accounts, hours and cadences. Once loaded, later refresh failures
  // keep the scheduler usable.
  if (userId && query.isSuccess && query.isFetchedAfterMount) loadedUser.current.ready = true;
  return { defaults: query.data ?? null, isReady: loadedUser.current.ready };
}
